import React from 'react';
import { Zap, Battery, Power, Lightbulb, Wrench, Gauge } from 'lucide-react';
import { services } from '../data/mock';

const iconMap = {
  Zap,
  Battery,
  Power,
  Lightbulb,
  Wrench,
  Gauge
};

export const Services = () => {
  return (
    <section id="services" className="py-24 bg-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-50 rounded-full filter blur-3xl opacity-50 -z-0"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-50 rounded-full filter blur-3xl opacity-50 -z-0"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Our Services
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Comprehensive Auto Care Services
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From complex electrical diagnostics to routine maintenance, we've got you covered with expert service you can trust.
          </p>
        </div>

        {/* Featured Service Banner */}
        <div className="mb-16 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 to-gray-900/70 rounded-3xl"></div>
          <img
            src="https://images.unsplash.com/photo-1767990495521-95cceb571125"
            alt="Electrical system diagnostics"
            className="w-full h-[400px] object-cover rounded-3xl"
          />
          <div className="absolute inset-0 flex items-center">
            <div className="container mx-auto px-8">
              <div className="max-w-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center">
                    <Zap className="w-7 h-7 text-white" />
                  </div>
                  <span className="text-amber-400 font-bold text-lg">Specialty Service</span>
                </div>
                <h3 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  Expert Electrical System Diagnostics
                </h3>
                <p className="text-lg text-gray-200 mb-6">
                  We specialize in solving the complex electrical issues that stump other mechanics. Advanced diagnostic tools and decades of experience ensure we find and fix the problem right the first time.
                </p>
                <div className="flex items-center gap-6 text-white">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                    <span>Same-Day Service</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                    <span>Warranty Included</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => {
            const IconComponent = iconMap[service.icon];
            return (
              <div
                key={service.id}
                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200 hover:-translate-y-1"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-blue-50 group-hover:from-blue-500 group-hover:to-blue-600 rounded-xl flex items-center justify-center mb-6 transition-all duration-300 shadow-md">
                  <IconComponent className="w-7 h-7 text-blue-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {service.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-lg text-gray-600 mb-6">
            Don't see what you're looking for? We handle a wide range of automotive repairs.
          </p>
          <a
            href="tel:5037094366"
            className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-4 rounded-xl transition-all hover:scale-105 shadow-lg"
          >
            Call Us to Discuss Your Needs
          </a>
        </div>
      </div>
    </section>
  );
};
